import React from 'react';
import {
  type AdminAnalyticsFilterInput,
  type AdminAnalyticsStartedBy,
  useGetAdminAnalyticsAgencyProfileOptionsQuery
} from '~/api/graphql';
import { FEATURE_FLAGS, useFlag } from '~/components/feature-flags';
import {
  DEFAULT_ANALYTICS_AVG_ACCOUNT_PREMIUM,
  DEFAULT_ANALYTICS_HOURLY_RATE
} from '~/components/settings/analytics/analytics-constants';
import { effectiveAssumptions } from '~/components/settings/analytics/incremental-revenue';
import {
  type AnalyticsProfileOption,
  findProfileOption
} from '~/pages/settings/analytics-segment-filters';

export const ANALYTICS_FILTER_STORAGE_KEY_V3 = 'cara_admin_analytics_view_v3';
export const ANALYTICS_FILTER_STORAGE_KEY_V4 = 'cara_admin_analytics_view_v4';

export type AnalyticsPeriod = 'last7' | 'last30' | 'last90' | 'lastYear' | 'all' | 'custom';

export type SubagencyScope =
  | { kind: 'parent' }
  | { kind: 'all' }
  | { kind: 'child'; agencyId: string };

/** An access profile, or the users no profile governs. */
export type AgencyProfileSelection =
  | { kind: 'profile'; agencyProfileId: string }
  | { kind: 'none' };

export type AnalyticsViewState = {
  period: AnalyticsPeriod;
  customAfter?: string;
  customBefore?: string;
  agencyUserId?: string;
  agencyProfile?: AgencyProfileSelection;
  startedBy?: AdminAnalyticsStartedBy;
  subagencyScope?: SubagencyScope;
  hourlyRate?: number;
  avgAccountPremium?: number;
};

/**
 * The view state's segment filters, in the order the pickers and chips show them. The User
 * filter predates the analytics_segment_filters_enabled gate, which adds the others.
 */
export const SEGMENT_FILTER_KEYS = [
  'agencyProfile',
  'agencyUserId',
  'startedBy'
] as const satisfies readonly (keyof AnalyticsViewState)[];

export type SegmentFilterKey = (typeof SEGMENT_FILTER_KEYS)[number];

export function effectiveSubagencyScope(state: AnalyticsViewState): SubagencyScope {
  return state.subagencyScope ?? { kind: 'parent' };
}

export function subagencyScopesEqual(a: SubagencyScope, b: SubagencyScope): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'child' && b.kind === 'child') {
    return a.agencyId === b.agencyId;
  }
  return true;
}

function startOfTodayUTC(): Date {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
}

function daysAgoUTC(days: number): Date {
  const start = startOfTodayUTC();
  return new Date(start.getTime() - days * 24 * 60 * 60 * 1000);
}

export function parseISODate(dateStr: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

// viewStateToFilter only sets the fields that filter something.
function hasAnalyticsFilterFields(out: AdminAnalyticsFilterInput): boolean {
  return Object.keys(out).length > 0;
}

/** Sets the field, or removes it when value is undefined, so stored state has no empty keys. */
function setOrDelete<K extends keyof AnalyticsViewState>(
  state: AnalyticsViewState,
  key: K,
  value: AnalyticsViewState[K] | undefined
): AnalyticsViewState {
  const next = { ...state };
  if (value === undefined) {
    delete next[key];
  } else {
    next[key] = value;
  }
  return next;
}

/** The view state without the filters the analytics_segment_filters_enabled gate adds. */
function withoutGatedSegmentFilters(state: AnalyticsViewState): AnalyticsViewState {
  const next = { ...state };
  for (const key of SEGMENT_FILTER_KEYS) {
    if (key !== 'agencyUserId') delete next[key];
  }
  return next;
}

export function viewStateToFilter(state: AnalyticsViewState): {
  filter: AdminAnalyticsFilterInput | undefined;
  customRangeError: string | null;
} {
  const out: AdminAnalyticsFilterInput = {};
  let customRangeError: string | null = null;

  if (state.agencyUserId) {
    out.agencyUserId = state.agencyUserId;
  }
  if (state.agencyProfile?.kind === 'profile') {
    out.agencyProfileId = state.agencyProfile.agencyProfileId;
  } else if (state.agencyProfile?.kind === 'none') {
    out.withoutAgencyProfile = true;
  }
  if (state.startedBy) {
    out.startedBy = state.startedBy;
  }

  const scope = effectiveSubagencyScope(state);
  if (scope.kind === 'all') {
    out.includeAllSubagencies = true;
  } else if (scope.kind === 'child') {
    out.subagencyId = scope.agencyId;
  }

  if (state.period === 'all') {
    return { filter: hasAnalyticsFilterFields(out) ? out : undefined, customRangeError };
  }

  if (state.period === 'custom') {
    const start = state.customAfter ? parseISODate(state.customAfter) : null;
    const end = state.customBefore ? parseISODate(state.customBefore) : null;
    if (start) {
      out.occurredAt = { ...out.occurredAt, after: start };
    }
    if (end) {
      if (start && end < start) {
        customRangeError = 'End date must be on or after start date';
      } else {
        // DB filter is exclusive (sq.Lt); add a day so the end date is inclusive.
        const before = new Date(end.getTime() + 24 * 60 * 60 * 1000);
        out.occurredAt = { ...out.occurredAt, before };
      }
    }
    return {
      filter: hasAnalyticsFilterFields(out) ? out : undefined,
      customRangeError
    };
  }

  let after: Date | null = null;
  switch (state.period) {
    case 'last7':
      after = daysAgoUTC(7);
      break;
    case 'last30':
      after = daysAgoUTC(30);
      break;
    case 'last90':
      after = daysAgoUTC(90);
      break;
    case 'lastYear':
      after = daysAgoUTC(365);
      break;
    default:
      break;
  }

  if (after) {
    out.occurredAt = { after };
  }

  return { filter: out, customRangeError };
}

function defaultViewState(): AnalyticsViewState {
  return {
    period: 'last30',
    hourlyRate: DEFAULT_ANALYTICS_HOURLY_RATE,
    avgAccountPremium: DEFAULT_ANALYTICS_AVG_ACCOUNT_PREMIUM
  };
}

function migrateViewStateAssumptions(state: AnalyticsViewState): AnalyticsViewState {
  const legacy = state as AnalyticsViewState & { avgPolicyAmount?: number };
  if (state.avgAccountPremium == null && legacy.avgPolicyAmount != null) {
    return { ...state, avgAccountPremium: legacy.avgPolicyAmount };
  }
  return state;
}

function applyAssumptionDefaults(state: AnalyticsViewState): AnalyticsViewState {
  const migrated = migrateViewStateAssumptions(state);
  return {
    ...migrated,
    hourlyRate: migrated.hourlyRate ?? DEFAULT_ANALYTICS_HOURLY_RATE,
    avgAccountPremium: migrated.avgAccountPremium ?? DEFAULT_ANALYTICS_AVG_ACCOUNT_PREMIUM
  };
}

function loadViewState(): AnalyticsViewState {
  if (typeof window === 'undefined') {
    return defaultViewState();
  }
  try {
    const rawV4 = localStorage.getItem(ANALYTICS_FILTER_STORAGE_KEY_V4);
    if (rawV4) {
      const parsed = JSON.parse(rawV4) as AnalyticsViewState;
      if (parsed && typeof parsed.period === 'string') {
        return applyAssumptionDefaults(parsed);
      }
    }
    const rawV3 = localStorage.getItem(ANALYTICS_FILTER_STORAGE_KEY_V3);
    if (rawV3) {
      const parsed = JSON.parse(rawV3) as AnalyticsViewState;
      if (parsed && typeof parsed.period === 'string') {
        return applyAssumptionDefaults(parsed);
      }
    }
  } catch {
    // ignore corrupt storage
  }
  return defaultViewState();
}

function saveViewState(state: AnalyticsViewState) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(ANALYTICS_FILTER_STORAGE_KEY_V4, JSON.stringify(state));
  } catch {
    // ignore quota errors
  }
}

export function useAnalyticsFilters() {
  const [viewState, setViewState] = React.useState<AnalyticsViewState>(loadViewState);
  const segmentFiltersEnabled = useFlag(FEATURE_FLAGS.ANALYTICS_SEGMENT_FILTERS);

  React.useEffect(() => {
    saveViewState(viewState);
  }, [viewState]);

  const appliedViewState = React.useMemo(
    () => (segmentFiltersEnabled ? viewState : withoutGatedSegmentFilters(viewState)),
    [viewState, segmentFiltersEnabled]
  );
  const { filter, customRangeError } = React.useMemo(
    () => viewStateToFilter(appliedViewState),
    [appliedViewState]
  );
  const hasSegmentFilter = SEGMENT_FILTER_KEYS.some((key) => appliedViewState[key] != null);

  const setPeriod = React.useCallback((period: AnalyticsPeriod) => {
    setViewState((prev) => ({ ...prev, period }));
  }, []);

  const setCustomAfter = React.useCallback((customAfter: string) => {
    setViewState((prev) => ({ ...prev, period: 'custom', customAfter }));
  }, []);

  const setCustomBefore = React.useCallback((customBefore: string) => {
    setViewState((prev) => ({ ...prev, period: 'custom', customBefore }));
  }, []);

  const setAgencyUserId = React.useCallback((agencyUserId: string | undefined) => {
    setViewState((prev) => setOrDelete(prev, 'agencyUserId', agencyUserId));
  }, []);

  const setAgencyProfile = React.useCallback(
    (agencyProfile: AgencyProfileSelection | undefined) => {
      setViewState((prev) => setOrDelete(prev, 'agencyProfile', agencyProfile));
    },
    []
  );

  const setStartedBy = React.useCallback((startedBy: AdminAnalyticsStartedBy | undefined) => {
    setViewState((prev) => setOrDelete(prev, 'startedBy', startedBy));
  }, []);

  // Each agency has its own users and profiles, so changing agency clears the picks.
  const setSubagencyScope = React.useCallback((scope: SubagencyScope) => {
    setViewState((prev) => {
      const prevScope = effectiveSubagencyScope(prev);
      const next: AnalyticsViewState = { ...prev, subagencyScope: scope };
      if (
        !subagencyScopesEqual(prevScope, scope) &&
        (scope.kind === 'all' || scope.kind === 'child' || prevScope.kind === 'child')
      ) {
        delete next.agencyUserId;
        delete next.agencyProfile;
      }
      return next;
    });
  }, []);

  const setHourlyRate = React.useCallback((hourlyRate: number | undefined) => {
    setViewState((prev) => {
      const next = { ...prev };
      if (hourlyRate != null && hourlyRate > 0) {
        next.hourlyRate = hourlyRate;
      } else {
        delete next.hourlyRate;
      }
      return next;
    });
  }, []);

  const setAvgAccountPremium = React.useCallback((avgAccountPremium: number | undefined) => {
    setViewState((prev) => {
      const next = { ...prev };
      if (avgAccountPremium != null && avgAccountPremium > 0) {
        next.avgAccountPremium = avgAccountPremium;
      } else {
        delete next.avgAccountPremium;
      }
      return next;
    });
  }, []);

  const profileOptions = useAnalyticsProfileOptions(
    viewState,
    segmentFiltersEnabled,
    setAgencyProfile
  );
  const selectedProfileOption = findProfileOption(profileOptions ?? [], viewState.agencyProfile);

  const assumptions = React.useMemo(
    () =>
      effectiveAssumptions({
        hourlyRate: viewState.hourlyRate,
        avgAccountPremium: viewState.avgAccountPremium
      }),
    [viewState.hourlyRate, viewState.avgAccountPremium]
  );

  return {
    viewState,
    filter,
    customRangeError,
    assumptions,
    segmentFiltersEnabled,
    hasSegmentFilter,
    profileOptions,
    selectedProfileOption,
    setPeriod,
    setCustomAfter,
    setCustomBefore,
    setAgencyUserId,
    setAgencyProfile,
    setStartedBy,
    setSubagencyScope,
    setHourlyRate,
    setAvgAccountPremium
  };
}

/**
 * The Access profile filter's options, or undefined when it isn't offered: without the gate, or
 * across all subagencies, which each have their own profiles. The options are empty for an agency
 * without the Enterprise feature. Clears a picked profile the options lack, such as an archived one.
 */
function useAnalyticsProfileOptions(
  viewState: AnalyticsViewState,
  segmentFiltersEnabled: boolean,
  setAgencyProfile: (agencyProfile: AgencyProfileSelection | undefined) => void
): readonly AnalyticsProfileOption[] | undefined {
  const scope = effectiveSubagencyScope(viewState);
  const canFilterByProfile = segmentFiltersEnabled && scope.kind !== 'all';
  const { data } = useGetAdminAnalyticsAgencyProfileOptionsQuery({
    variables: { subagencyId: scope.kind === 'child' ? scope.agencyId : undefined },
    skip: !canFilterByProfile,
    fetchPolicy: 'cache-and-network'
  });
  const profileOptions = canFilterByProfile ? data?.adminAnalyticsAgencyProfileOptions : undefined;

  const isStaleSelection =
    viewState.agencyProfile != null &&
    (!canFilterByProfile ||
      (profileOptions != null && !findProfileOption(profileOptions, viewState.agencyProfile)));
  React.useEffect(() => {
    if (isStaleSelection) setAgencyProfile(undefined);
  }, [isStaleSelection, setAgencyProfile]);

  return profileOptions;
}
